import React, { useState, useEffect } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { observer } from "mobx-react";
import StyleVariables from "../../assets/styles/Variable.module.scss";
import {
  DateBuddhistEra as DateBuddhistEraUtil,
  DateTimeBuddhistEra as DateTimeBuddhistEraUtil,
} from "../../utils/DateFormat";
import dayjs from "dayjs";
import { NumericFormat } from "react-number-format";
import { KeysType as KeysTypePaymentConstant } from "../../constants/Payment";
import { ProductWeightType as ProductWeightTypeConstant } from "../../constants/Gold";
import SouvenirsBill from "./Souvenirs";
import type { BillSouvenir as BillSouvenirModel } from "../../models/Souvenir";
import { Exchange as ExchangeModel } from "../../models/Exchange";
import { Payment as PaymentModel } from "../../models/Payment";

type Props = {
  souvenirs?: BillSouvenirModel[];
  exchange: ExchangeModel | null;
  payments: PaymentModel[];
};

const Exchange = observer((props: Props) => {
  const [payments, setPayments] = useState<any[]>([]);

  useEffect(() => {
    let mappingPayment = [];
    let totalCash =
      props.exchange?.paymentType === KeysTypePaymentConstant.allCash ||
      props.exchange?.paymentType === KeysTypePaymentConstant.mix
        ? props.payments.find((item) => item.channelCode === "99")?.amount || 0
        : 0;
    let totalQrCode = props.payments
      ?.filter((item) => item.channelName.toLocaleLowerCase().search("qr") >= 0)
      .reduce((n, { amount }) => n + amount, 0);
    let totalTransfer = props.payments
      ?.filter(
        (item) => item.channelName.toLocaleLowerCase().search("ลูกค้าโอน") >= 0
      )
      .reduce((n, { amount }) => n + amount, 0);
    let totalCreditCard = props.payments
      ?.filter(
        (item, index) =>
          item.channelName.toLocaleLowerCase().search("qr") < 0 &&
          item.channelName.toLocaleLowerCase().search("ลูกค้าโอน") < 0 &&
          item.channelName.toLocaleLowerCase().search("เฟิร์สช้อยส์") < 0 &&
          item.channelName.toLocaleLowerCase().search("อิออน") < 0 &&
          item.channelCode !== "99"
      )
      .reduce((n, { amount }) => n + amount, 0);
    let totalFirstChoice = props.payments
      ?.filter(
        (item) =>
          item.channelName.toLocaleLowerCase().search("เฟิร์สช้อยส์") >= 0 ||
          item.channelName.toLocaleLowerCase().search("อิออน") >= 0
      )
      .reduce((n, { amount }) => n + amount, 0);

    totalCash > 0 &&
      mappingPayment.push({ channelName: "เงินสด", amount: totalCash });
    totalQrCode > 0 &&
      mappingPayment.push({ channelName: "QR Code", amount: totalQrCode });
    totalTransfer > 0 &&
      mappingPayment.push({ channelName: "เงินโอน", amount: totalTransfer });
    totalCreditCard > 0 &&
      mappingPayment.push({
        channelName: "บัตรเครดิต",
        amount: totalCreditCard,
      });
    totalFirstChoice > 0 &&
      mappingPayment.push({
        channelName: "เฟิร์สช้อยส์/อิออน",
        amount: totalFirstChoice,
      });
    setPayments([...mappingPayment]);
  }, [props.payments, props.exchange?.paymentType]);

  return (
    <Box color={StyleVariables["color-text"]}>
      {/* Heading */}
      <Box borderBottom={`1px dashed ${StyleVariables["color-border"]}`}>
        {/* Branch Name && Ref Invoice Number */}
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          gap={1}
          marginBottom={1}
        >
          <Typography variant="subtitle2" fontWeight={"regular"}>
            {props.exchange?.branchName}
          </Typography>
          <Typography variant="subtitle2" fontWeight={"regular"}>
            {props.exchange?.refInvoiceNo || "X-XXXXXXXX-XXXX"}
          </Typography>
        </Box>
        {/* /.Branch Name && Ref Invoice Number */}

        {/* Create Date && Employee Name */}
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          gap={1}
          marginBottom={2}
        >
          <Typography variant="subtitle2" fontWeight={"regular"}>
            {props.exchange?.userFirstName}{" "}
            {props.exchange?.userLastName && props.exchange?.userLastName[0]}.
          </Typography>
          <Typography variant="subtitle2" fontWeight={"regular"}>
            {props.exchange?.createdAt
              ? DateTimeBuddhistEraUtil(props.exchange?.createdAt)
              : `${DateBuddhistEraUtil(dayjs())} --:--`}
          </Typography>
        </Box>
        {/* /.Create Date && Employee Name */}
      </Box>
      {/* /.Heading */}

      {/* Product List */}
      <Box marginTop={2}>
        {/* Old */}
        <Grid container spacing={1} alignItems={"center"}>
          <Grid item xs={7}>
            <Typography variant="subtitle2" fontWeight={"regular"}>
              ทองเก่า: {props.exchange?.exchangeOldHasItems[0]?.productName}{" "}
              จำนวน {props.exchange?.oldTotalQuantity} ชิ้น
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={"regular"}
              marginTop={1}
            >
              {`(นน.รวม${parseFloat(
                props.exchange?.oldTotalWeight.toFixed(3) || "0"
              )} กรัม)`}
            </Typography>
          </Grid>
        </Grid>
        {/* /.Old */}

        {/* New */}
        <Grid container spacing={1} alignItems={"center"} marginTop={1}>
          <Grid item xs={7}>
            <Typography variant="subtitle2" fontWeight={"regular"}>
              ทองใหม่: {props.exchange?.exchangeNewHasItems[0]?.productName}{" "}
              จำนวน {props.exchange?.newTotalQuantity} ชิ้น
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={"regular"}
              marginTop={1}
            >
              {props.exchange?.exchangeNewHasItems[0]?.productType ===
              ProductWeightTypeConstant.equalWeight
                ? `(นน.${parseFloat(
                    props.exchange?.exchangeNewHasItems[0]?.weight.toFixed(3)
                  )} กรัม/ชิ้น)`
                : `(นน.รวม${parseFloat(
                    props.exchange?.newTotalWeight.toFixed(3) || "0"
                  )} กรัม)`}
            </Typography>
          </Grid>
          <Grid
            item
            xs={5}
            textAlign={"right"}
            sx={{ wordBreak: "break-word" }}
          >
            <Typography variant="subtitle2" fontWeight={"regular"}>
              <NumericFormat
                value={props.exchange?.netPay}
                displayType="text"
                thousandSeparator
              />
            </Typography>
          </Grid>
        </Grid>
        {/* /.New */}
      </Box>
      {/* /.Product List */}

      {/* Payment Detail */}
      <Box
        marginY={2}
        paddingBottom={2}
        borderBottom={`1px dashed ${StyleVariables["color-border"]}`}
      >
        <Typography variant="subtitle2" fontWeight={"regular"}>
          รับเงินจากลูกค้าโดย
        </Typography>
        {payments.map((item, index) => {
          return (
            <Grid key={index} container marginTop={1}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" fontWeight={"regular"}>
                  {item.channelName}
                </Typography>
              </Grid>
              <Grid item xs={6} textAlign={"right"}>
                <Typography variant="subtitle2" fontWeight={"regular"}>
                  <NumericFormat
                    value={item.amount}
                    displayType="text"
                    thousandSeparator
                  />
                </Typography>
              </Grid>
            </Grid>
          );
        })}
      </Box>
      {/* Payment Detail */}

      {/* Souvenirs */}
      <SouvenirsBill souvenirs={props.souvenirs} />
      {/* /.Souvenirs */}

      {/* Detail for Staff */}
      <Box marginTop={2} color={StyleVariables["color-placeholder"]}>
        <Typography variant="subtitle2" fontWeight={"bold"}>
          รายละเอียดต่างๆ
        </Typography>
        {/* Current Sell - Purchase Price */}
        <Typography variant="subtitle2" fontWeight={"regular"} marginTop={1}>
          <NumericFormat
            value={props.exchange?.goldSellPrice}
            displayType="text"
            thousandSeparator
          />{" "}
          -{" "}
          <NumericFormat
            value={props.exchange?.goldPurchasePrice}
            displayType="text"
            thousandSeparator
          />
        </Typography>
        {/* /.Current Sell - Purchase Price */}

        {/* Gold Price */}
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          gap={1}
          marginTop={1}
        >
          <Typography variant="subtitle2" fontWeight={"regular"}>
            ราคาเนื้อทอง
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight={"regular"}
            textAlign={"right"}
          >
            <NumericFormat
              value={props.exchange?.newTotalGoldPrice}
              displayType="text"
              decimalScale={2}
              thousandSeparator
            />
          </Typography>
        </Box>
        {/* /.Gold Price */}

        {/* Labour */}
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          gap={1}
          marginTop={1}
          color={
            props.exchange?.currentStatus !== "poor"
              ? StyleVariables["color-placeholder"]
              : StyleVariables["color-error"]
          }
        >
          <Typography variant="subtitle2" fontWeight={"regular"}>
            ค่ากำเหน็จ
          </Typography>
          <Typography
            variant="subtitle2"
            fontWeight={"regular"}
            textAlign={"right"}
          >
            <NumericFormat
              value={props.exchange?.newTotalLabour}
              displayType="text"
              decimalScale={2}
              thousandSeparator
            />
          </Typography>
        </Box>
        {/* /.Labour */}
      </Box>
      {props.exchange?.currentStatus === "poor" && (
        <>
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            gap={1}
            marginTop={1}
            color={StyleVariables["color-error"]}
          >
            <Typography variant="subtitle2" fontWeight={"regular"}>
              ค่ากำเหน็จมาตรฐาน
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={"regular"}
              textAlign={"right"}
            >
              <NumericFormat
                value={
                  props.exchange?.newTotalLabourStandard -
                  props.exchange?.totalExtraDiscountStandard
                }
                displayType="text"
                thousandSeparator
              />
            </Typography>
          </Box>
          <Typography
            variant="subtitle2"
            fontWeight={"medium"}
            color={StyleVariables["color-error"]}
            marginTop={2}
          >
            กรุณาตรวจสอบราคาอีกครั้ง
          </Typography>
        </>
      )}
      {/* Purchase Price */}
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        gap={1}
        marginTop={1}
        color={StyleVariables["color-placeholder"]}
      >
        <Typography variant="subtitle2" fontWeight={"regular"}>
          ราคารับซื้อทองเก่า
        </Typography>
        <Typography
          variant="subtitle2"
          fontWeight={"regular"}
          textAlign={"right"}
        >
          <NumericFormat
            value={props.exchange?.oldTotalPrice}
            displayType="text"
            decimalScale={2}
            thousandSeparator
          />
        </Typography>
      </Box>
      {/* /.Purchase Price */}

      {/* /.Detail for Staff */}
    </Box>
  );
});

export default Exchange;
